import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { connectRefinementList } from 'react-instantsearch-dom';
import Select from 'react-select';
import { ErrorText } from '@styled';
import fonts from '@general/fonts';

/**
 * Connect Algolia RefinementList to react-select
 *
 * @param transformItems              Gets all the items in the select on mount. Sort or filter here, then return the items
 * @param customOptionTransform       Gets a selected option, do something to it and return it
 *
 * @docs https://www.algolia.com/doc/api-reference/widgets/refinement-list/react/#connector
 * @docs https://react-select.com/props
 */
const RefinementSelect = props => {
    const {
        // Algolia connectRefinementList
        items,
        limit,
        refine,
        currentRefinement,
        attribute,
        refinementRef,
        placeholder,
        // ICSC
        customOptionTransform,
        customReverseOptionTransform,
        // react-select
        onMenuOpen,
    } = props;
    const colourStyles = {
        control: styles => ({
            ...styles,
            backgroundColor: 'white',
            borderRadius: 0,
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: '#e4eaef',
            height: '56px',
            paddingLeft: '.7rem',
        }),
        input: styles => ({
            ...styles,
            fontFamily: fonts.sans,
        }),
        menuList: styles => ({
            ...styles,
            fontFamily: fonts.sans,
        }),
        placeholder: styles => ({ ...styles, color: '#464a4c', fontFamily: fonts.sans }),
        singleValue: styles => ({ ...styles, fontFamily: fonts.sans }),
        valueContainer: styles => ({
            ...styles,
            padding: '0px',
            flexWrap: 'nowrap',
            fontFamily: fonts.sans,
        }),
    };

    useEffect(() => {
        if (refinementRef) {
            refinementRef.current = {
                attribute,
                refine,
            };
        }
    }, [refine, refinementRef, attribute]);

    const onChange = (selected, { action, option, removedValue }) => {
        if (option) {
            refine(option.value);
        }

        if (removedValue) {
            if (items.length > 0) {
                const toRemove = items.find(item => item.label === removedValue);
                refine(toRemove.value);
            } else if (currentRefinement.length > 0) {
                if (customReverseOptionTransform) {
                    const [formattedRemovedValue] = customReverseOptionTransform([removedValue]);
                    refine(currentRefinement.filter(item => item !== formattedRemovedValue));
                } else {
                    refine(currentRefinement.filter(item => item !== removedValue));
                }
            }
        }
    };

    const capitalizeOptionLabel = option => {
        if (typeof option === 'object') {
            return option.label.charAt(0).toUpperCase() + option.label.slice(1);
        }

        return option.charAt(0).toUpperCase() + option.slice(1);
    };

    return (
        <Select
            limit={limit}
            name={attribute}
            value={customOptionTransform ? customOptionTransform(currentRefinement) : currentRefinement}
            onChange={onChange}
            onMenuOpen={onMenuOpen}
            options={items}
            getOptionLabel={capitalizeOptionLabel}
            styles={colourStyles}
            isClearable={false}
            isSearchable
            isMulti
            placeholder={placeholder}
            noOptionsMessage={() => <ErrorText>No options</ErrorText>}
        />
    );
};

RefinementSelect.propTypes = {
    attribute: PropTypes.string,
    items: PropTypes.array,
    limit: PropTypes.number,
    refine: PropTypes.func,
    currentRefinement: PropTypes.array,
    placeholder: PropTypes.string,
    customOptionTransform: PropTypes.func,
    onMenuOpen: PropTypes.func,
};

export default connectRefinementList(RefinementSelect);
